// COMP-PATIENT-COVERAGE (AP-INS-01؛ US-INS-020 تا 022، 030، 031): پوشش‌های بیمه‌ای مراجعه‌کننده با بیمه‌گر، طرح، شماره بیمه، بازه
// اعتبار، اولویت استفاده و وضعیت تأیید؛ به‌علاوه استعلام استحقاق و تأیید دستی با مدرک.
// پوشش بدون قرارداد فعال مرکز هشدار می‌گیرد، چون سهم بیمه آن صفر با علت NoContract خواهد بود (BR-INS-001)؛ و پوشش تأییدنشده هشدار ریسک
// کسورات دارد که پذیرش را مسدود نمی‌کند (BR-INS-031).
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  unwrap,
  type InsInsuredRole,
  type InsManualEvidenceKind,
  type InsPatientCoverageView,
} from '@phoenix/api-client';
import { useAccess, useIdempotencyKey, useTenantSession, useWriteAccess } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  DataTable,
  faDigits,
  FormDialog,
  JalaliDate,
  JalaliDatePickerRHF,
  Money,
  SelectRHF,
  StatusChip,
  TextFieldRHF,
  zRequiredText,
  type DataTableColumn,
} from '@phoenix/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { eligibilityTone, EVIDENCE_KINDS, insuranceKeys, INSURED_ROLES, verificationTone } from './common';

export const COMP_PATIENT_COVERAGE = 'COMP-PATIENT-COVERAGE';

export function PatientCoveragePanel({ patientId }: { patientId: string }) {
  const { t } = useTranslation('ins');
  const { tenantApi, session } = useTenantSession();
  const access = useAccess();
  const write = useWriteAccess('ins.coverage.manage');
  const [creating, setCreating] = useState(false);
  const [checking, setChecking] = useState<InsPatientCoverageView | null>(null);
  const canManage = access.can('ins.coverage.manage') && write.canWrite;
  const coverages = useQuery({
    queryKey: insuranceKeys.coverages(session?.tenantId, patientId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/insurance/patient-coverages', { params: { query: { patientId } } })),
  });

  const columns: DataTableColumn<InsPatientCoverageView>[] = [
    {
      id: 'insurer',
      headerKey: 'ins:coverages.columns.insurer',
      cell: (row) => (
        <Stack spacing={0.25}>
          <Typography variant="body2">{row.insurerName}</Typography>
          <Typography variant="caption">{t(`ins:insurer_kind.${row.kind}`)}</Typography>
        </Stack>
      ),
    },
    { id: 'plan', headerKey: 'ins:coverages.columns.plan', cell: (row) => row.planName },
    { id: 'member', headerKey: 'ins:coverages.columns.member', cell: (row) => row.memberNumber },
    {
      id: 'role',
      headerKey: 'ins:coverages.columns.role',
      cell: (row) => t(`ins:insured_role.${row.role}`),
    },
    {
      id: 'validity',
      headerKey: 'ins:coverages.columns.validity',
      cell: (row) => (
        <Stack spacing={0.25}>
          <JalaliDate value={row.validFrom} />
          {row.validTo ? <JalaliDate value={row.validTo} /> : <Typography variant="caption">{t('ins:coverages.open_ended')}</Typography>}
        </Stack>
      ),
    },
    {
      id: 'priority',
      headerKey: 'ins:coverages.columns.priority',
      cell: (row) => faDigits(Number(row.priority)),
      align: 'right',
    },
    {
      id: 'verification',
      headerKey: 'ins:coverages.columns.verification',
      cell: (row) => (
        <Stack spacing={0.5} data-testid={`ins-coverage-state-${row.coverageId}`}>
          <StatusChip tone={verificationTone(row.verification)} labelKey={`ins:verification.${row.verification}`} />
          {!row.hasActiveContract && (
            <StatusChip tone="pending" labelKey="ins:coverages.no_contract" />
          )}
        </Stack>
      ),
    },
    {
      id: 'eligibility',
      headerKey: 'ins:coverages.columns.eligibility',
      cell: (row) =>
        row.lastEligibility ? (
          <Stack spacing={0.25} data-testid={`ins-coverage-eligibility-${row.coverageId}`}>
            <StatusChip
              tone={eligibilityTone(row.lastEligibility.result)}
              labelKey={`ins:eligibility_result.${row.lastEligibility.result}`}
            />
            <Typography variant="caption">
              <JalaliDate value={row.lastEligibility.validForDate} />
            </Typography>
            {row.lastEligibility.remainingCap && (
              <Typography variant="caption">
                <Money amountMinor={Number(row.lastEligibility.remainingCap.amount)} />
              </Typography>
            )}
          </Stack>
        ) : (
          <Typography variant="caption">{t('ins:coverages.never_checked')}</Typography>
        ),
    },
    {
      id: 'actions',
      headerKey: 'ins:coverages.columns.actions',
      cell: (row) =>
        access.can('ins.eligibility.check') && row.isActive ? (
          <Button size="small" onClick={() => setChecking(row)} data-testid={`ins-coverage-check-${row.coverageId}`}>
            {t('ins:coverages.check')}
          </Button>
        ) : null,
    },
  ];

  const unverified = (coverages.data ?? []).some((row) => row.isActive && row.verification === 'Unverified');

  return (
    <Stack spacing={1.5} data-screen-id={COMP_PATIENT_COVERAGE}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h6">{t('ins:coverages.title')}</Typography>
        {canManage && (
          <Button variant="outlined" onClick={() => setCreating(true)} data-testid="ins-coverage-add">
            {t('ins:coverages.add')}
          </Button>
        )}
      </Stack>
      {unverified && (
        <Alert severity="warning" data-testid="ins-coverage-unverified-warning">
          {t('ins:coverages.unverified_warning')}
        </Alert>
      )}
      <DataTable
        captionKey="ins:coverages.caption"
        columns={columns}
        rows={coverages.data}
        getRowId={(row) => row.coverageId}
        isLoading={coverages.isPending}
        error={coverages.error}
        onRetry={() => void coverages.refetch()}
        emptyTitleKey="ins:coverages.empty"
      />
      {creating && <CoverageDialog patientId={patientId} onClose={() => setCreating(false)} />}
      {checking && <EligibilityDialog coverage={checking} onClose={() => setChecking(null)} />}
    </Stack>
  );
}

const coverageSchema = z
  .object({
    insurerId: z.string().min(1, 'common:validation.required'),
    planId: z.string().min(1, 'common:validation.required'),
    memberNumber: zRequiredText(64),
    role: z.string(),
    validFrom: z.string().min(1, 'common:validation.required'),
    priority: z.string(),
  })
  .superRefine((form, ctx) => {
    if (!(Number(form.priority) >= 0 && Number(form.priority) <= 100)) {
      ctx.addIssue({ code: 'custom', path: ['priority'], message: 'ins:coverages.priority_invalid' });
    }
  });

type CoverageForm = z.input<typeof coverageSchema>;

function CoverageDialog({ patientId, onClose }: { patientId: string; onClose: () => void }) {
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const insurers = useQuery({
    queryKey: insuranceKeys.insurers(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/insurance/insurers')),
  });
  const plans = useQuery({
    queryKey: insuranceKeys.plans(session?.tenantId),
    queryFn: () => unwrap(tenantApi.GET('/api/v1/insurance/plans')),
  });
  const form = useForm<CoverageForm>({
    resolver: zodResolver(coverageSchema),
    defaultValues: { insurerId: '', planId: '', memberNumber: '', role: 'Principal', validFrom: '', priority: '0' },
  });
  const insurerId = form.watch('insurerId');

  const save = useMutation({
    mutationFn: (values: CoverageForm) =>
      unwrap(
        tenantApi.POST('/api/v1/insurance/patient-coverages', {
          headers: { 'Idempotency-Key': idempotency.current() },
          body: {
            patientId,
            insurerId: values.insurerId,
            planId: values.planId,
            memberNumber: values.memberNumber,
            role: values.role as InsInsuredRole,
            principalPatientId: null,
            validFrom: values.validFrom,
            validTo: null,
            cardDocumentId: null,
            priority: Number(values.priority),
          },
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: insuranceKeys.coverages(session?.tenantId, patientId) });
      onClose();
    },
    onError: (error) => {
      const applied = applyServerErrors(error, form.setError, { fields: ['memberNumber', 'validFrom', 'priority'] });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      titleKey="ins:coverages.add_title"
      onClose={onClose}
      onSubmit={form.handleSubmit((values) => save.mutate(values))}
      isDirty={form.formState.isDirty}
      isSubmitting={save.isPending}
      submitError={submitError}
    >
      <Stack spacing={2}>
        <SelectRHF
          control={form.control}
          name="insurerId"
          labelKey="ins:coverages.fields.insurer"
          options={(insurers.data?.items ?? []).map((insurer) => ({ value: insurer.insurerId, label: insurer.name }))}
          data-testid="ins-coverage-insurer"
        />
        <SelectRHF
          control={form.control}
          name="planId"
          labelKey="ins:coverages.fields.plan"
          options={(plans.data ?? [])
            .filter((plan) => !insurerId || plan.insurerId === insurerId)
            .map((plan) => ({ value: plan.planId, label: plan.name }))}
          data-testid="ins-coverage-plan"
        />
        <TextFieldRHF
          control={form.control}
          name="memberNumber"
          labelKey="ins:coverages.fields.member_number"
          data-testid="ins-coverage-member"
        />
        <SelectRHF
          control={form.control}
          name="role"
          labelKey="ins:coverages.fields.role"
          options={INSURED_ROLES.map((role) => ({ value: role, labelKey: `ins:insured_role.${role}` }))}
          data-testid="ins-coverage-role"
        />
        <span data-testid="ins-coverage-valid-from">
          <JalaliDatePickerRHF control={form.control} name="validFrom" labelKey="ins:coverages.fields.valid_from" />
        </span>
        <TextFieldRHF
          control={form.control}
          name="priority"
          labelKey="ins:coverages.fields.priority"
          helperKey="ins:coverages.fields.priority_hint"
          type="number"
          data-testid="ins-coverage-priority"
        />
      </Stack>
    </FormDialog>
  );
}

const manualSchema = z.object({
  evidenceKind: z.string(),
  reference: zRequiredText(100),
});

type ManualForm = z.input<typeof manualSchema>;

/**
 * استعلام استحقاق (US-INS-030) و تأیید دستی با مدرک (US-INS-031): نتیجه نامعلوم پذیرش را مسدود نمی‌کند و فقط هشدار ریسک کسورات است
 * (BR-INS-031).
 */
function EligibilityDialog({ coverage, onClose }: { coverage: InsPatientCoverageView; onClose: () => void }) {
  const { t } = useTranslation('ins');
  const { tenantApi, session } = useTenantSession();
  const queryClient = useQueryClient();
  const idempotency = useIdempotencyKey();
  const [submitError, setSubmitError] = useState<unknown>();
  const form = useForm<ManualForm>({
    resolver: zodResolver(manualSchema),
    defaultValues: { evidenceKind: 'InsuranceCard', reference: '' },
  });

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: insuranceKeys.coverages(session?.tenantId, coverage.patientId) });
    onClose();
  };

  const check = useMutation({
    mutationFn: () =>
      unwrap(
        tenantApi.POST('/api/v1/insurance/patient-coverages/{id}:check-eligibility', {
          params: {
            path: { id: coverage.coverageId },
            query: { patientId: coverage.patientId },
            },
            headers: { 'Idempotency-Key': idempotency.current() },
        }),
      ),
    onSuccess: invalidate,
  });

  const confirm = useMutation({
    mutationFn: (values: ManualForm) =>
      unwrap(
        tenantApi.POST('/api/v1/insurance/patient-coverages/{id}:confirm-eligibility', {
          params: {
            path: { id: coverage.coverageId },
            query: { patientId: coverage.patientId },
            },
            headers: { 'Idempotency-Key': idempotency.current() },
          body: { evidenceKind: values.evidenceKind as InsManualEvidenceKind, reference: values.reference, at: null },
        }),
      ),
    onSuccess: invalidate,
    onError: (error) => {
      const applied = applyServerErrors(error, form.setError, { fields: ['reference'] });
      setSubmitError(applied > 0 ? undefined : error);
    },
  });

  return (
    <FormDialog
      open
      titleKey="ins:coverages.check_title"
      onClose={onClose}
      onSubmit={form.handleSubmit((values) => confirm.mutate(values))}
      isDirty={form.formState.isDirty}
      isSubmitting={check.isPending || confirm.isPending}
      submitError={submitError ?? check.error}
      submitKey="ins:coverages.confirm_manually"
    >
      <Stack spacing={2}>
        <Typography variant="body2">{t('ins:coverages.check_hint')}</Typography>
        <Button
          variant="contained"
          onClick={() => check.mutate()}
          disabled={check.isPending}
          data-testid="ins-coverage-check-online"
        >
          {t('ins:coverages.check_online')}
        </Button>
        <Typography variant="subtitle2">{t('ins:coverages.manual_section')}</Typography>
        <SelectRHF
          control={form.control}
          name="evidenceKind"
          labelKey="ins:coverages.fields.evidence"
          options={EVIDENCE_KINDS.map((kind) => ({ value: kind, labelKey: `ins:evidence_kind.${kind}` }))}
          data-testid="ins-coverage-evidence"
        />
        <TextFieldRHF
          control={form.control}
          name="reference"
          labelKey="ins:coverages.fields.reference"
          helperKey="ins:coverages.fields.reference_hint"
          data-testid="ins-coverage-reference"
        />
      </Stack>
    </FormDialog>
  );
}
